import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePricingQuery } from '@xver/hooks/usePricingQuery';

import { ConfigForEstimate, estimateMonthly, pricingFromResponse } from '../utils/pricing';

interface PricingSummaryProps {
    config: ConfigForEstimate;
    pricingType: 'request_based' | 'instance_based';
    compact?: boolean;
}

function formatBaht(value: number): string {
    return value.toLocaleString('th-TH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function formatNumber(value: number): string {
    return value.toLocaleString('th-TH');
}

export default function PricingSummary({
    config,
    pricingType,
    compact = false,
}: PricingSummaryProps) {
    const { t } = useTranslation('containerEngine');

    const { data: pricingResp } = usePricingQuery();
    const pricing = useMemo(() => pricingFromResponse(pricingResp), [pricingResp]);
    const estimate = useMemo(
        () => estimateMonthly(config, pricingType, pricing),
        [config, pricingType, pricing]
    );

    const showRange = config.min_replicas !== config.max_replicas;
    const isRequestBased = pricingType === 'request_based';

    return (
        <div className={`w-full flex flex-col ${compact ? 'gap-3' : 'gap-4'}`}>
            <div className="w-full p-3 rounded-md border border-line bg-surface-2">
                <h3 className="text-sm font-semibold text-fg/85 mb-2">
                    {t('deployment.pricingSummary.howItWorks.title', 'How billing works')}
                </h3>
                <ul className="flex flex-col gap-1.5 text-xs text-fg/68 list-disc pl-4">
                    <li>
                        {t(
                            'deployment.pricingSummary.howItWorks.billingCycle',
                            'Billed hourly per container. Cost = observed usage × rate, after subtracting the free tier.'
                        )}
                    </li>
                    <li>
                        {t(
                            'deployment.pricingSummary.howItWorks.freeTier',
                            'Free tier is granted per container, per calendar month, and resets on the 1st.'
                        )}
                    </li>
                    <li>
                        {t(
                            'deployment.pricingSummary.howItWorks.consumption',
                            'Pricing model (request-based vs instance-based) only changes Knative scaling behaviour. Both modes use the same rates.'
                        )}
                    </li>
                    {!compact && (
                        <>
                            <li>
                                {t(
                                    'deployment.pricingSummary.howItWorks.scaleToZero',
                                    'Request-based with min replicas = 0 idles at 0 ฿; you only pay while requests are being handled.'
                                )}
                            </li>
                            <li>
                                {t(
                                    'deployment.pricingSummary.howItWorks.insufficientFunds',
                                    'If your credit runs out, the account is suspended. Containers are deleted after a 24-hour grace period.'
                                )}
                            </li>
                        </>
                    )}
                </ul>
            </div>

            <div
                className={`w-full grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-3`}
            >
                <div className="w-full p-3 rounded-md border border-line bg-surface-2">
                    <h3 className="text-sm font-semibold text-fg/85 mb-2">
                        {t('deployment.pricingSummary.rates', 'Rates')}
                    </h3>
                    <ul className="flex flex-col gap-1 text-sm text-fg/78">
                        <li>
                            {t('deployment.pricingSummary.cpuRate', '{{rate}} ฿ / vCPU-second', {
                                rate: pricing.CPUSecondsRate,
                            })}
                        </li>
                        <li>
                            {t('deployment.pricingSummary.memoryRate', '{{rate}} ฿ / GiB-second', {
                                rate: pricing.GiBSecondsRate,
                            })}
                        </li>
                        <li>
                            {t(
                                'deployment.pricingSummary.requestRate',
                                '{{rate}} ฿ / 1M requests',
                                { rate: pricing.HTTPRequestsRate }
                            )}
                        </li>
                        <li>
                            {t(
                                'deployment.pricingSummary.volumeRate',
                                '{{rate}} ฿ / GB-month (block storage)',
                                { rate: pricing.VolumeGBMonthlyRate }
                            )}
                        </li>
                    </ul>
                </div>

                <div className="w-full p-3 rounded-md border border-line bg-surface-2">
                    <h3 className="text-sm font-semibold text-fg/85 mb-2">
                        {t('deployment.pricingSummary.freeTier', 'Free tier (per month)')}
                    </h3>
                    <ul className="flex flex-col gap-1 text-sm text-fg/78">
                        <li>
                            {t('deployment.pricingSummary.freeVcpu', '{{value}} vCPU-seconds', {
                                value: formatNumber(pricing.FreeVCPUSeconds),
                            })}
                        </li>
                        <li>
                            {t('deployment.pricingSummary.freeMemory', '{{value}} GiB-seconds', {
                                value: formatNumber(pricing.FreeGiBSeconds),
                            })}
                        </li>
                        <li>
                            {t(
                                'deployment.pricingSummary.freeRequests',
                                '{{value}} HTTP requests',
                                { value: formatNumber(pricing.FreeHTTPRequests) }
                            )}
                        </li>
                    </ul>
                </div>
            </div>

            <div className="w-full p-4 rounded-md border border-line bg-surface-1">
                <h3 className="text-sm font-semibold text-fg/85 mb-2">
                    {t('deployment.pricingSummary.estimateTitle', 'Estimated monthly cost')}
                </h3>
                <div className="w-full flex flex-col gap-1.5">
                    <div className="flex flex-row justify-between items-center">
                        <span className="text-sm text-fg/68">
                            {t(
                                'deployment.pricingSummary.estimateMinLabel',
                                'Min replicas always-on'
                            )}
                        </span>
                        <span className="text-base font-bold text-fg">
                            {formatBaht(estimate.minMonthlyCost)} ฿
                        </span>
                    </div>
                    {showRange && (
                        <div className="flex flex-row justify-between items-center">
                            <span className="text-sm text-fg/68">
                                {t(
                                    'deployment.pricingSummary.estimateMaxLabel',
                                    'Max replicas always-on'
                                )}
                            </span>
                            <span className="text-base font-bold text-fg">
                                {formatBaht(estimate.maxMonthlyCost)} ฿
                            </span>
                        </div>
                    )}
                </div>
                {isRequestBased && (
                    <p className="mt-1 text-xs text-fg/68">
                        {t(
                            'deployment.pricingSummary.requestBasedNote',
                            'Costs apply only beyond the free tier. Scale-to-zero (min replicas = 0) keeps idle cost at 0 ฿.'
                        )}
                    </p>
                )}
            </div>

            <p className="text-xs italic text-fg/68">
                {t(
                    'deployment.pricingSummary.disclaimer',
                    'Estimates exclude network egress, HTTP-request charges and block storage. Volumes are added after the container is created and billed separately.'
                )}
            </p>
        </div>
    );
}
